import React from 'react';
import '../../styles/PerformanceMetrics.css';

/**
 * PerformanceMetrics Component
 *
 * Displays key performance indicators:
 * - Occupancy Rate (معدل الإشغال)
 * - Average Daily Rate (متوسط السعر اليومي)
 * - Total Revenue (إجمالي الإيرادات)
 * - Total Bookings (إجمالي الحجوزات)
 */
const PerformanceMetrics = ({ metrics, comparison, loading, toDate = false, crossRefMonth = false }) => {
  if (loading) {
    return (
      <div className="performance-metrics loading" dir="rtl">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  const formatNumber = (num) => {
    return num?.toLocaleString('en-US', { maximumFractionDigits: 2 }) || '0';
  };

  const formatPercentage = (num) => {
    return `${formatNumber(num)}%`;
  };

  const renderTrend = (comparison, metric) => {
    if (!comparison || !comparison[metric]) return null;

    const { change, direction } = comparison[metric];

    // Unchanged metric: muted, no arrow — not a decline
    if (direction === 'neutral') {
      return (
        <span className="trend-indicator trend-neutral">
          {formatPercentage(0)}
        </span>
      );
    }

    const isPositive = direction === 'up';
    const icon = isPositive ? 'bi-arrow-up' : 'bi-arrow-down';
    const colorClass = isPositive ? 'trend-up' : 'trend-down';

    return (
      <span className={`trend-indicator ${colorClass}`}>
        <i className={`bi ${icon}`}></i>
        {formatPercentage(Math.abs(change))}
      </span>
    );
  };

  return (
    <div className="performance-metrics" dir="rtl">
      {/* Occupancy Rate Card */}
      <div className="metric-card occupancy">
        <div className="metric-icon">
          <i className="bi bi-calendar-check"></i>
        </div>
        <div className="metric-content">
          <h3>{formatPercentage(metrics.occupancy?.rate || 0)}</h3>
          <p>معدل الإشغال{toDate ? ' (حتى اليوم)' : ''}</p>
          <small className="metric-details">
            {formatNumber(metrics.occupancy?.bookedNights || 0)} / {formatNumber(metrics.occupancy?.totalNights || 0)} ليلة
          </small>
          {/* An in-progress period counts only elapsed nights. The ops cross-reference is
              added only for "this month", the one period ops reports side by side. */}
          {toDate && (
            <small className="metric-note">
              الليالي المنقضية فقط{crossRefMonth ? ' · مؤشر أداء العمليات يعرض الشهر كاملاً' : ''}
            </small>
          )}
          {renderTrend(comparison, 'occupancy')}
        </div>
      </div>

      {/* Average Daily Rate Card */}
      <div className="metric-card adr">
        <div className="metric-icon">
          <i className="bi bi-currency-exchange"></i>
        </div>
        <div className="metric-content">
          <h3>{formatNumber(metrics.rates?.adr || 0)} ر.س</h3>
          <p>
            متوسط السعر اليومي
            <span className="info-icon-wrapper">
              <i className="bi bi-info-circle info-icon"></i>
              <span className="info-tooltip">
                إجمالي الإيرادات ÷ إجمالي الليالي المحجوزة<br/>
                مثال: 1000 ر.س ÷ 5 ليالي = 200 ر.س لكل ليلة
              </span>
            </span>
          </p>
          <small className="metric-details">
            السعر لكل ليلة محجوزة
          </small>
          {metrics.rates?.highestAdr > 0 && (
            <small className="metric-details">
              الأعلى: {formatNumber(metrics.rates.highestAdr)} ر.س · الأدنى: {formatNumber(metrics.rates.lowestAdr)} ر.س
            </small>
          )}
          {renderTrend(comparison, 'adr')}
        </div>
      </div>

      {/* Gross Revenue Card */}
      <div className="metric-card revenue">
        <div className="metric-icon">
          <i className="bi bi-cash-stack"></i>
        </div>
        <div className="metric-content">
          <h3>{formatNumber(metrics.revenue?.gross || 0)} ر.س</h3>
          <p>إجمالي الإيرادات</p>
          <div className="revenue-breakdown">
            <small>رسوم المنصات: {formatNumber(metrics.revenue?.platformCommissions || 0)} ر.س</small>
            <small>صافي الإيراد: <strong>{formatNumber(metrics.revenue?.net || 0)} ر.س</strong></small>
          </div>
          {renderTrend(comparison, 'revenue')}
        </div>
      </div>

      {/* Total Bookings Card */}
      <div className="metric-card bookings">
        <div className="metric-icon">
          <i className="bi bi-house-check"></i>
        </div>
        <div className="metric-content">
          <h3>{formatNumber(metrics.bookings?.total || 0)}</h3>
          <p>إجمالي الحجوزات</p>
          <div className="booking-stats">
            {metrics.bookings?.averageStayLength > 0 && (
              <small>
                متوسط الإقامة: {formatNumber(metrics.bookings?.averageStayLength || 0)} ليالي
                <span className="info-icon-wrapper">
                  <i className="bi bi-info-circle info-icon"></i>
                  <span className="info-tooltip">
                    مجموع ليالي الإقامة الكاملة ÷ عدد الحجوزات<br/>
                    يشمل كامل ليالي الحجز حتى لو امتدت خارج الفترة المحددة
                  </span>
                </span>
              </small>
            )}
            {metrics.bookings?.averageLeadTime > 0 && (
              <small>
                متوسط وقت الحجز: {formatNumber(metrics.bookings?.averageLeadTime || 0)} يوم
                <span className="info-icon-wrapper">
                  <i className="bi bi-info-circle info-icon"></i>
                  <span className="info-tooltip">
                    عدد الأيام بين تاريخ الحجز وتاريخ الوصول<br/>
                    مثال: حجز في 1 يناير لوصول في 15 يناير = 14 يوم
                  </span>
                </span>
              </small>
            )}
          </div>
          {renderTrend(comparison, 'bookings')}
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;
